import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createViewAction,
  getForm,
  getRegister,
  formatDateTime,
  addSelectItem,
  createApproveAction,
} from "../../utils/helpers";
import { URL_LIQUIDATION_REQUESTS } from "../../utils/urls";
import useGet from "../common/useGet";
import {
  API_LIQUIDATION_REQUESTS,
  API_SYSTEM_ACCOUNTS,
  API_TRANSACTABLE_ACCOUNTS_DETAILS,
} from "../../utils/apis";
import { IsLiquidationRequestApprover } from "../../utils/permissions";

// Helper: Resolve Account Name by ID

const resolveAccountName = (accountId: any, accountsData: any) => {
  if (!accountId || !accountsData) return "Unknown Account";
  const accountsArray = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  if (!accountsArray.length) return "Loading...";
  const account = accountsArray.find((a) => a.id === accountId);
  return account ? account.account_name : "Unknown Account";
};

// Helper: Format Amount
const formatAmount = (amount: string | number) => {
  return `UGX ${Number(amount).toLocaleString("en-US")}`;
};

export const useLiquidationRequests = () => {
  const isLiquidationRequestApprover = IsLiquidationRequestApprover();
  // Get system accounts data
  const {
    loadData: loadAccountsData,
    loading: loadingAccounts,
    data: accountsData,
  } = useGet();

  useEffect(() => {
    loadAccountsData({ api: API_SYSTEM_ACCOUNTS });
  }, []);

  const tableColumns = () => [
    createColumn("account_name", true, "Impacted Account"),
    createColumn("amount", false, "Amount"),
    createColumn("action", false, "Action"),
    createColumn("status", false, "Status"),
    createColumn("created_at", false, "Created Date"),
    createColumn("actions", false, "Actions"),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => ({
      ...item,
      account_name: item?.impacted_account_name || "N/A",
      amount: formatAmount(item?.amount || 0),
      action: item?.action || "N/A",
      status: item?.status,
      created_at: item?.created_at ? formatDateTime(item.created_at) : "N/A",
      actions: (
        <FormActions
          actions={[
            createViewAction(URL_LIQUIDATION_REQUESTS, item?.id),
            ...(!isLiquidationRequestApprover && item.status === "PENDING"
              ? [
                  createApproveAction(
                    `${API_LIQUIDATION_REQUESTS}${item?.id}/`,
                    item?.impacted_account_name,
                    true,
                  ),
                ]
              : []),
          ]}
        />
      ),
    }));
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Liquidation Request Details",
          fields: {
            impacted_account: resolveAccountName(
              item?.impacted_account,
              accountsData,
            ),
            amount: formatAmount(item?.amount || 0),
            action: item?.action || "N/A",
            status: item?.status || "PENDING",
            maker_notes: item?.maker_notes || "No notes provided",
            checker_notes: item?.checker_notes || "No checker notes",
            gl_status: item?.gl_status ? "Posted to GL" : "Not posted to GL",
            declaration: item?.declaration ? "Yes" : "No",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name} (${item.created_by.email})`
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
            maker: item?.maker || "N/A",
            checker: item?.checker || "N/A",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_LIQUIDATION_REQUESTS);
  const activityFilter = "liquidation_requests";

  return {
    tableData,
    tableColumns,
    viewData,
    formRoute,
    activityFilter,
    accountsData,
    loadingAccounts,
  };
};

export const useLiquidationRequestForm = ({}: TFormHookParams = {}) => {
  // Get system accounts data
  const {
    loadData: loadTransactableAccountsData,
    loading: loadingTransactableAccounts,
    data: accountsTransactableData,
  } = useGet();

  useEffect(() => {
    loadTransactableAccountsData({ api: API_TRANSACTABLE_ACCOUNTS_DETAILS });
  }, []);

  const actionOptions = [
    addSelectItem({
      label: "Settle",
      value: "SETTLE",
    }),
    addSelectItem({
      label: "Write Off",
      value: "WRITE_OFF",
    }),
  ];

  const fields = [
    {
      field_name: "impacted_account",
      label: "Impacted Account",
      type: "select",
      props: { required: true },
      options_data: accountsTransactableData?.map((account: any) =>
        addSelectItem({
          label: account.account_name,
          value: account.account_code,
        }),
      ),
      loading: loadingTransactableAccounts,
      visible: true,
    },
    {
      field_name: "amount",
      label: "Amount (UGX)",
      type: "number",
      props: {
        required: true,
        min: 1,
        step: 0.01,
        placeholder: "Enter amount",
      },
      visible: true,
    },
    {
      field_name: "action",
      label: "Action",
      type: "select",
      props: { required: true },
      options_data: actionOptions,
      visible: true,
    },
    {
      field_name: "maker_notes",
      label: "Maker Notes",
      type: "textarea",
      props: {
        rows: 3,
        placeholder: "Enter details about this liquidation request",
      },
      visible: true,
    },
  ];

  const sections: IFormSection[] = [
    {
      title: "Liquidation Request Details",
      fields: fields,
    },
  ];

  const redirect = getRegister(URL_LIQUIDATION_REQUESTS);

  const defaultValues = {};

  const onSubmit = (payload: any, id: string) => {
    if (id) {
      return {
        id,
        api: API_LIQUIDATION_REQUESTS,
        params: {
          ...payload,
        },
      };
    }
    return {
      api: API_LIQUIDATION_REQUESTS,
      params: {
        ...payload,
      },
    };
  };

  return {
    fields,
    sections,
    redirect,
    defaultValues,
    accountsTransactableData,
    loadingTransactableAccounts,
    onSubmit,
  };
};

export const LiquidationRequestForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { fields } = useLiquidationRequestForm({
    form,
    updateFormValues: updateFormValue,
  });

  // Filter visible fields
  const visibleFields = fields.filter((field: any) => field.visible !== false);

  return (
    <FormFields
      fields={visibleFields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};
